import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Alert, message } from "antd";
import Header from "../layouts/Header";
import Sidebar from "../layouts/Sidebar";
import { rolesApi, usersApi } from "../../apis";

const adminSchema = yup.object({
  firstName: yup
    .string()
    .required("First name is required")
    .trim(),
  lastName: yup
    .string()
    .required("Last name is required")
    .trim(),
  email: yup
    .string()
    .email("Invalid email format")
    .required("Email is required")
    .trim(),
  phone: yup
    .string()
    .required("Phone number is required")
    .test(
      "phone-format",
      "Invalid phone number. Enter 9 digits after +20 (e.g., 1001234567)",
      (value) => {
        if (!value) return false;
        return /^\d{9}$/.test(value);
      },
    ),
  password: yup
    .string()
    .required("Password is required")
    .min(6, "Minimum 6 characters"),
  role: yup.string().required("Role is required"),
});

const titleCase = (value = "") =>
  value
    .split(/[_\-.]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const AddAdmin = () => {
  const history = useHistory();
  const [menu, setMenu] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [roles, setRoles] = useState([]);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(adminSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      phone: "",
      role: "",
    },
    mode: "onChange",
  });

  const phoneValue = watch("phone");
  const selectedRoleName = watch("role");
  const selectedRole = useMemo(
    () => roles.find((role) => role.name === selectedRoleName) || null,
    [roles, selectedRoleName],
  );

  useEffect(() => {
    let active = true;

    const loadRoles = async () => {
      try {
        setRolesLoading(true);
        const response = await rolesApi.getAll();
        if (!active) return;

        const allRoles = (response || []).sort((a, b) =>
          a.name.localeCompare(b.name),
        );

        setRoles(allRoles);
        if (allRoles.length) {
          setValue("role", allRoles[0].name, { shouldValidate: true });
        }
      } catch (error) {
        if (!active) return;
        const msg = error.response?.data?.message;
        if (Array.isArray(msg)) msg.forEach((item) => message.error(item));
        else message.error(msg || "Failed to load roles");
      } finally {
        if (active) setRolesLoading(false);
      }
    };

    loadRoles();
    return () => {
      active = false;
    };
  }, [setValue]);

  const handlePhoneChange = (e) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value.length > 9) {
      value = value.slice(0, 9);
    }
    setValue("phone", value, { shouldValidate: true });

    if (errors.phone) {
      clearErrors("phone");
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);

      if (!/^\d{9}$/.test(data.phone)) {
        setError("phone", {
          type: "manual",
          message:
            "Invalid phone number. Enter 9 digits after +20 (e.g., 1001234567)",
        });
        setLoading(false);
        return;
      }

      const payload = {
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        email: data.email.trim(),
        password: data.password,
        role: data.role,
        phone: `+20${data.phone}`,
      };

      await usersApi.create(payload);
      message.success(
        `${data.firstName} ${data.lastName} created with role ${titleCase(
          data.role,
        )} successfully`,
      );
      history.push("/users/admins");
    } catch (err) {
      const msg = err.response?.data?.message;
      if (Array.isArray(msg)) msg.forEach((m) => message.error(m));
      else message.error(msg || "Failed to create user");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
      <Header onMenuClick={() => setMenu(!menu)} />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="card mb-0">
            <div className="card-body">
              <div className="page-header">
                <div className="content-page-header">
                  <h5>Add Dashboard User</h5>
                  <button
                    type="button"
                    className="btn btn-primary cancel"
                    onClick={() => history.push("/users/admins")}
                    disabled={loading}
                  >
                    Back
                  </button>
                </div>
              </div>


                <form onSubmit={handleSubmit(onSubmit)} noValidate>
                  <div className="form-group-item">
                    <h5 className="form-title">Personal Information</h5>
                    <div className="row">
                      <div className="col-lg-4 col-md-6">
                        <div className="input-block mb-3">
                          <label>
                            First Name <span className="text-danger">*</span>
                          </label>
                          <input
                            className={`form-control ${
                              errors.firstName ? "is-invalid" : ""
                            }`}
                            placeholder="Ahmed"
                            {...register("firstName")}
                            disabled={loading}
                          />
                          {errors.firstName && (
                            <small className="text-danger d-block mt-1">
                              {errors.firstName.message}
                            </small>
                          )}
                        </div>
                      </div>

                      <div className="col-lg-4 col-md-6">
                        <div className="input-block mb-3">
                          <label>
                            Last Name <span className="text-danger">*</span>
                          </label>
                          <input
                            className={`form-control ${
                              errors.lastName ? "is-invalid" : ""
                            }`}
                            placeholder="Hassan"
                            {...register("lastName")}
                            disabled={loading}
                          />
                          {errors.lastName && (
                            <small className="text-danger d-block mt-1">
                              {errors.lastName.message}
                            </small>
                          )}
                        </div>
                      </div>

                      <div className="col-lg-4 col-md-6">
                        <div className="input-block mb-3">
                          <label>
                            Phone <span className="text-danger">*</span>
                          </label>
                          <div className="input-group">
                            <span className="input-group-text bg-white">
                              <span className="me-1">🇪🇬</span> +20
                            </span>
                            <input
                              type="tel"
                              className={`form-control ${
                                errors.phone ? "is-invalid" : ""
                              }`}
                              placeholder="1001234567"
                              value={phoneValue}
                              onChange={handlePhoneChange}
                              disabled={loading}
                            />
                          </div>
                          {errors.phone && (
                            <small className="text-danger d-block mt-1">
                              {errors.phone.message}
                            </small>
                          )}
                          <small className="text-muted d-block mt-1">
                            Enter 9 digits after +20 (e.g., 1001234567)
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="form-group-item mt-2">
                    <h5 className="form-title">Account Details</h5>
                    <div className="row">
                      <div className="col-lg-4 col-md-6">
                        <div className="input-block mb-3">
                          <label>
                            Email <span className="text-danger">*</span>
                          </label>
                          <input
                            type="email"
                            className={`form-control ${
                              errors.email ? "is-invalid" : ""
                            }`}
                            placeholder="ahmed@example.com"
                            {...register("email")}
                            disabled={loading}
                          />
                          {errors.email && (
                            <small className="text-danger d-block mt-1">
                              {errors.email.message}
                            </small>
                          )}
                        </div>
                      </div>

                      <div className="col-lg-4 col-md-6">
                        <div className="input-block mb-3">
                          <label>
                            Password <span className="text-danger">*</span>
                          </label>
                          <input
                            type="password"
                            className={`form-control ${
                              errors.password ? "is-invalid" : ""
                            }`}
                            placeholder="Min. 6 characters"
                            {...register("password")}
                            disabled={loading}
                            autoComplete="new-password"
                          />
                          {errors.password && (
                            <small className="text-danger d-block mt-1">
                              {errors.password.message}
                            </small>
                          )}
                          <small className="text-muted d-block mt-1">
                            Minimum 6 characters
                          </small>
                        </div>
                      </div>

                      <div className="col-lg-4 col-md-6">
                        <div className="input-block mb-3">
                          <label>
                            Role <span className="text-danger">*</span>
                          </label>
                          <select
                            className={`form-control ${
                              errors.role ? "is-invalid" : ""
                            }`}
                            {...register("role")}
                            disabled={loading || rolesLoading}
                          >
                            {roles.map((role) => (
                              <option key={role.id} value={role.name}>
                                {titleCase(role.name)}
                              </option>
                            ))}
                          </select>
                          {errors.role && (
                            <small className="text-danger d-block mt-1">
                              {errors.role.message}
                            </small>
                          )}
                          <small className="text-muted d-block mt-1">
                            All available roles are shown.
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>

                  {selectedRole && (
                    <Alert
                      className="mb-4"
                      type="info"
                      showIcon
                      message={`Selected role: ${titleCase(selectedRole.name)}`}
                      description={
                        selectedRole.description ||
                        `${titleCase(selectedRole.name)} has ${
                          (selectedRole.permissions || []).length
                        } permissions.`
                      }
                    />
                  )}

                  <div className="form-group-item border-0">
                    <div className="text-end">
                      <button
                        type="button"
                        className="btn btn-primary cancel me-2"
                        onClick={() => history.push("/users/admins")}
                        disabled={loading}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Creating User…
                          </>
                        ) : (
                          "Create User"
                        )}
                      </button>
                    </div>
                  </div>
                </form>
            
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddAdmin;
